import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { JSX } from "react";

/**
 * The ambient field the whole sign-in sits on — the customer support portal's
 * `PremiumBackdrop`, rebuilt in MUI and recoloured.
 *
 * Four layers, in the reference's order and for the reference's reasons:
 *
 *   1. two soft radial glows (accent upper-left, XOP lime lower-right);
 *   2. a fine dot field, masked to a soft ellipse so it never reaches the
 *      edges of the page and turns into wallpaper;
 *   3. long curved strokes that fade in and out along their own length — the
 *      "network" motif, drawn as gradients with transparent bands rather than
 *      as dashes, so a line appears to pass behind something and re-emerge;
 *   4. in dark mode, an inner vignette that pulls the corners down so the
 *      white top bar and the contact footer both have something to sit on.
 *
 * The one changed variable is hue: where the reference glows brand blue, this
 * glows `palette.accent` (teal). Lime is kept in the lower glow exactly as the
 * reference keeps it — it is the shared XOP signature, and dropping it would
 * have made the two portals *less* obviously related rather than more
 * distinguishable.
 *
 * Everything is `aria-hidden` and `pointer-events: none`: it is a painting,
 * not a control.
 *
 * ## `anchor`
 *
 * Every layer here is sized against its own container: the glows are `vh`
 * circles pinned by percentage offsets, the dot field is masked to an
 * ellipse centred at 50%/50%, and the network lines stretch a 1000×800
 * viewBox over the full box with `preserveAspectRatio: none`. On a
 * one-screen page — the sign-in, the onboarding wall, the invitation page —
 * that container IS the viewport, which is what the drawing assumes, so
 * `anchor="container"` (the default, `position: absolute; inset: 0`) is
 * correct there and stays byte-identical.
 *
 * The public site's pages are three screens tall, and there the same
 * absolute box is three screens tall too: the lower-right glow lands below
 * the fold and is clipped away, the dot ellipse centres on the middle of
 * the DOCUMENT rather than on anything the reader is looking at, and the
 * lines smear over 3000px. `anchor="viewport"` switches the outer box to
 * `position: fixed; inset: 0` so all four layers are laid out against the
 * viewport and stay put while the page scrolls — the ambient field the
 * drawing was designed to be, on a page of any length.
 *
 * The fixed box keeps `zIndex: 0`, so the layout's positioned siblings
 * (`TopBar` at 3, `<main>` at 1, the footer at 2) all still paint above it.
 */
export function Backdrop({ anchor = "container" }: { anchor?: "container" | "viewport" } = {}): JSX.Element {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const { accent, secondary } = theme.palette;

  /* The glow reads at a different strength on near-black than on near-white:
     on a light canvas it has to stay a wash, on a dark one it is the only
     colour in the frame and can carry more. */
  const glowAccent = dark ? accent[500] : accent[400];
  const accentStops = dark
    ? `${alpha(glowAccent, 0.4)} 0%, ${alpha(glowAccent, 0.14)} 35%, transparent 70%`
    : `${alpha(glowAccent, 0.42)} 0%, ${alpha(accent[200], 0.3)} 35%, transparent 70%`;
  const limeStops = dark
    ? `${alpha(secondary.main, 0.32)} 0%, ${alpha(secondary.main, 0.12)} 40%, transparent 70%`
    : `${alpha(secondary.main, 0.22)} 0%, ${alpha(secondary.main, 0.1)} 40%, transparent 70%`;

  /* The dot field goes dark-on-light and light-on-dark, and is masked to an
     ellipse in both. `WebkitMaskImage` is still required for Safari. */
  const dotColor = dark ? alpha(accent[200], 0.8) : alpha(theme.palette.text.primary, 0.1);
  const dotMask = "radial-gradient(ellipse 75% 65% at 50% 50%, black 25%, transparent 88%)";

  const lineColor = dark ? alpha(accent[400], 0.35) : alpha(accent[600], 0.18);

  return (
    <Box
      aria-hidden
      sx={{ pointerEvents: "none", position: anchor === "viewport" ? "fixed" : "absolute", inset: 0, overflow: "hidden", zIndex: 0 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "-28%",
          left: "-10%",
          height: "105vh",
          width: "105vh",
          borderRadius: "50%",
          filter: dark ? "blur(110px)" : "blur(90px)",
          opacity: 0.9,
          backgroundImage: `radial-gradient(circle, ${accentStops})`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-30%",
          right: "-10%",
          height: "100vh",
          width: "100vh",
          borderRadius: "50%",
          filter: dark ? "blur(110px)" : "blur(90px)",
          opacity: dark ? 0.8 : 0.7,
          backgroundImage: `radial-gradient(circle, ${limeStops})`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: dark ? 0.18 : 0.65,
          backgroundImage: `radial-gradient(circle, ${dotColor} 1px, transparent 1.6px)`,
          backgroundSize: "22px 22px",
          maskImage: dotMask,
          WebkitMaskImage: dotMask,
        }}
      />

      <NetworkLines color={lineColor} />

      {dark && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse 90% 80% at 50% 50%, transparent 40%, ${alpha(
              theme.palette.common.black,
              0.5,
            )} 100%)`,
          }}
        />
      )}
    </Box>
  );
}

/**
 * The drifting stroke motif. Paths and gradient stops are the reference's,
 * unchanged — they are the drawing, and redrawing them by hand would only
 * make the two pages differ in a way nobody asked for. `preserveAspectRatio:
 * none` stretches the 1000×800 viewBox over whatever the viewport is, which
 * is what makes the strokes read as sweeps across the *page* rather than as a
 * figure of a fixed size.
 *
 * `vectorEffect="non-scaling-stroke"` keeps the line weight at ~1px however
 * far the box is stretched; without it a wide viewport smears the horizontals
 * into bands.
 */
function NetworkLines({ color }: { color: string }): JSX.Element {
  const stops = (offsets: ReadonlyArray<readonly [number, number]>) =>
    offsets.map(([offset, opacity]) => (
      <stop key={offset} offset={`${offset}%`} stopColor="currentColor" stopOpacity={opacity} />
    ));

  return (
    <Box
      component="svg"
      viewBox="0 0 1000 800"
      preserveAspectRatio="none"
      fill="none"
      stroke="currentColor"
      sx={{ position: "absolute", inset: 0, height: "100%", width: "100%", color }}
    >
      <defs>
        <linearGradient id="xopLineH" x1="0%" y1="0%" x2="100%" y2="0%">
          {stops([
            [0, 0],
            [12, 1],
            [28, 1],
            [38, 0],
            [48, 0],
            [60, 1],
            [78, 1],
            [92, 0],
            [100, 0],
          ])}
        </linearGradient>
        <linearGradient id="xopLineHr" x1="100%" y1="0%" x2="0%" y2="0%">
          {stops([
            [0, 0],
            [14, 1],
            [34, 1],
            [44, 0],
            [56, 0],
            [68, 1],
            [86, 1],
            [100, 0],
          ])}
        </linearGradient>
        <linearGradient id="xopLineD1" x1="0%" y1="0%" x2="100%" y2="100%">
          {stops([
            [0, 0],
            [20, 1],
            [42, 1],
            [55, 0],
            [68, 1],
            [88, 1],
            [100, 0],
          ])}
        </linearGradient>
        <linearGradient id="xopLineD2" x1="0%" y1="100%" x2="100%" y2="0%">
          {stops([
            [0, 0],
            [18, 1],
            [36, 0],
            [50, 1],
            [72, 1],
            [84, 0],
            [100, 0],
          ])}
        </linearGradient>
        <linearGradient id="xopLineSoft" x1="0%" y1="0%" x2="100%" y2="0%">
          {stops([
            [0, 0],
            [50, 1],
            [100, 0],
          ])}
        </linearGradient>
      </defs>

      {/* Long sweeps */}
      <path d="M -60 210 C 240 70, 540 380, 1060 150" stroke="url(#xopLineH)" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
      <path d="M 1060 320 C 740 200, 380 480, -60 340" stroke="url(#xopLineHr)" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
      <path d="M -60 480 C 280 420, 600 580, 1060 460" stroke="url(#xopLineH)" strokeWidth="1.1" vectorEffect="non-scaling-stroke" />
      <path d="M -60 660 C 320 760, 660 480, 1060 720" stroke="url(#xopLineHr)" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
      {/* Diagonals */}
      <path d="M 1080 -20 C 820 220, 380 360, -80 540" stroke="url(#xopLineD2)" strokeWidth="1.3" vectorEffect="non-scaling-stroke" />
      <path d="M 1080 580 C 760 360, 420 280, -80 220" stroke="url(#xopLineD1)" strokeWidth="1.1" vectorEffect="non-scaling-stroke" />
      {/* Short accents */}
      <path d="M 220 -30 Q 420 220, 720 60" stroke="url(#xopLineSoft)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <path d="M -40 760 Q 140 620, 320 740" stroke="url(#xopLineSoft)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <path d="M 760 40 Q 880 160, 1040 80" stroke="url(#xopLineSoft)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
    </Box>
  );
}
